import { useEffect, useRef, useState } from 'react';

/* ------------------------------------------------------------------ layout */

export const Card = ({ as: Tag = 'div', className = '', children, ...rest }) => (
  <Tag className={`card ${className}`.trim()} {...rest}>
    {children}
  </Tag>
);

export const Row = ({ className = '', children }) => (
  <div className={`row ${className}`.trim()}>{children}</div>
);

export const Grid2 = ({ children, className = '' }) => (
  <div className={`g2 ${className}`.trim()}>{children}</div>
);
export const Grid3 = ({ children, className = '' }) => (
  <div className={`g3 ${className}`.trim()}>{children}</div>
);

export const Muted = ({ children, className = '' }) => (
  <p className={`mu ${className}`.trim()}>{children}</p>
);

export const SectionTitle = ({ children }) => (
  <h2 style={{ margin: '32px 0 14px' }}>{children}</h2>
);

/* ----------------------------------------------------------------- buttons */

export function Button({ variant, size, busy, children, className = '', ...rest }) {
  const classes = ['btn', variant, size, className].filter(Boolean).join(' ');
  return (
    <button className={classes} disabled={busy || rest.disabled} {...rest}>
      {busy ? 'Working...' : children}
    </button>
  );
}

export const Link = ({ children, ...rest }) => (
  <button type="button" className="lk" {...rest}>
    {children}
  </button>
);

/* ------------------------------------------------------------------- state */

export const Tag = ({ tone = '', children }) => (
  <span className={`tag ${tone}`.trim()}>{children}</span>
);

/** The eligibility band shown next to a programme. */
export const band = (aggregate) =>
  aggregate <= 10 ? 'Very competitive' : aggregate <= 18 ? 'Strong' : aggregate <= 26 ? 'Good' : 'Limited options';

export const AggregateRing = ({ aggregate, total = 36 }) => (
  <div className="ring" style={{ '--p': `${Math.round(((total - aggregate) / (total - 6)) * 100)}%` }}>
    <span>{aggregate}</span>
  </div>
);

export const Steps = ({ current, items }) => (
  <div className="steps">
    {items.map((item, i) => (
      <span key={item} className={i < current ? 'dn' : i === current ? 'on' : ''}>
        {/* The number is the step's position, not a count of anything the stage
            is named after, so it reads "Step 2 of 5: Programmes" rather than
            "2 Programmes". Wrap it in its own element and let CSS draw it as a
            numeral badge so the two never run together on the line. */}
        <i className="sn">{i + 1}</i> <em>{item}</em>
      </span>
    ))}
  </div>
);

/* ------------------------------------------------------------------ states */

export const Spinner = ({ label = 'Loading' }) => (
  <div className="note" role="status">
    {label}...
  </div>
);

export const Empty = ({ title, children, action }) => (
  <div className="card" style={{ textAlign: 'center' }}>
    <h3 style={{ fontSize: 18 }}>{title}</h3>
    {children ? <Muted>{children}</Muted> : null}
    {action}
  </div>
);

export const Banner = ({ tone = '', title, children }) => (
  <div className="note">
    {title ? <b>{title}</b> : null}
    {children ? <div className="mu" style={{ marginTop: title ? 4 : 0 }}>{children}</div> : null}
  </div>
);

/**
 * Fetches on mount and whenever `deps` change, and exposes the usual four
 * states so a screen never has to hand-roll loading and error handling.
 */
export function useAsync(fn, deps = []) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const runId = useRef(0);

  const run = async () => {
    const id = ++runId.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await fn();
      // A slower earlier request must not overwrite a newer result.
      if (id === runId.current) setState({ data, loading: false, error: null });
    } catch (error) {
      if (id === runId.current) setState({ data: null, loading: false, error });
    }
  };

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { ...state, reload: run, setData: (data) => setState((s) => ({ ...s, data })) };
}

/* ------------------------------------------------------------------- forms */

export function Field({ label, hint, error, children, required }) {
  return (
    <label style={{ display: 'block' }}>
      {label}
      {required ? <span style={{ color: 'var(--no)' }}> *</span> : null}
      {hint ? <span className="mu" style={{ display: 'block', fontSize: 13 }}>{hint}</span> : null}
      <div style={{ marginTop: 5 }}>{children}</div>
      {error ? (
        <span style={{ color: 'var(--no)', fontSize: 13 }} role="alert">
          {error}
        </span>
      ) : null}
    </label>
  );
}

export const Text = ({ label, hint, error, required, ...rest }) => (
  <Field label={label} hint={hint} error={error} required={required}>
    <input {...rest} />
  </Field>
);

export const Area = ({ label, hint, error, required, ...rest }) => (
  <Field label={label} hint={hint} error={error} required={required}>
    <textarea rows={4} {...rest} />
  </Field>
);

export const Select = ({ label, hint, error, required, children, ...rest }) => (
  <Field label={label} hint={hint} error={error} required={required}>
    <select {...rest}>{children}</select>
  </Field>
);

/** Single-choice chips, matching the prototype's filter row. */
export function Chips({ options, value, onChange, label }) {
  return (
    <div role="group" aria-label={label}>
      {options.map((option) => {
        const key = typeof option === 'string' ? option : option.value;
        const text = typeof option === 'string' ? option : option.label;
        return (
          <button
            key={key}
            type="button"
            className={`chip ${value === key ? 'on' : ''}`}
            aria-pressed={value === key}
            onClick={() => onChange(key)}
          >
            {text}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------- utilities */

/** Formats a date for display, or returns a neutral dash when it is unset. */
export function fmtDate(value) {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function fmtDateTime(value) {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/** Today's date as YYYY-MM-DD for date inputs, in local time. */
export function today() {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

/**
 * Hand a fetched string to the browser as a file download.
 *
 * Downloads that need a signed-in user cannot use `window.location.href` or a
 * plain <a download>: the API takes a bearer token and sets no cookie, so the
 * browser would send the request with no credentials and get a 401. The request
 * has to be made by the app (so the token is attached), then the body is
 * wrapped in a blob and saved from memory.
 */
export function saveTextFile(contents, filename, type = 'text/csv;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([contents], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Releasing immediately can cancel the download in some browsers, so wait a
  // tick for the navigation to start first.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** A dated filename, so repeated exports do not silently overwrite each other. */
export const datedName = (name, extension) =>
  `${name}-${today().replace(/-/g, '')}.${extension}`;

export const initials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

/** The school monogram badge used throughout the prototype. */
export const Monogram = ({ children, className = '' }) => (
  <i className={`ub ${className}`.trim()}>{children}</i>
);

/* ------------------------------------------------------------------ modal */

/**
 * A real overlay dialog rather than a panel in the page flow.
 *
 * Everything that used to expand in place went here, because a long list pushed
 * the dialog itself below the fold: you had to scroll to the bottom of the page
 * to find a picker that had opened at the top. The body scrolls instead, the
 * dialog stays put, and Escape or the backdrop dismisses it.
 *
 * `footer` holds the actions so they stay visible while the body scrolls.
 */
export function Modal({ title, onClose, children, footer, labelledBy }) {
  const panel = useRef(null);

  // Escape closes, and Tab is held inside the dialog while it is open.
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
        return;
      }
      if (event.key !== 'Tab' || !panel.current) return;
      const focusable = panel.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    // Stop the page behind the dialog scrolling under it on touch devices.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Move focus in, so a keyboard user is inside the dialog rather than still
    // tabbing through the page behind it. Written as a statement, not a `??`
    // chain: focus() returns undefined, so a chain would always fall through to
    // the panel and immediately pull focus back off the autofocused field.
    const target = panel.current?.querySelector('[data-autofocus]') || panel.current;
    target?.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        ref={panel}
        tabIndex={-1}
      >
        <div className="row sp sheet-head">
          <h3 id={labelledBy} style={{ fontSize: 18, margin: 0 }}>{title}</h3>
          <Button variant="s" size="sm" onClick={onClose} aria-label="Close">Close</Button>
        </div>
        <div className="sheet-body">{children}</div>
        {footer ? <div className="sheet-foot">{footer}</div> : null}
      </div>
    </div>
  );
}
